import { type PairRouteMode, type PairSeedResponse, fetchPairSeed } from '../api/pairs'
import { loadPairPage } from './pairPagePreload'

// Hover/intent prefetch of the composed pair-page seed (/v1/pairs/seed).
//
// Freshness contract (owner rule): a prefetched seed may be consumed ONLY by
// the initial-seed path of usePairFeed, ONLY within PREFETCH_TTL_MS of its
// start, and the subscribe-before-seed barrier still runs exactly as without
// the prefetch — the seed promise is merely already in flight when the exact
// pool channels ACK. Coverage re-reads inside the barrier and every reconnect
// reseed always hit the network. A stale or unconsumed entry is discarded,
// never served.

// The measured click budget is ~300-800ms of hover lead; 2.5s keeps the seed
// inside the same realtime window the WS barrier reconciles anyway.
const PREFETCH_TTL_MS = 2_500
const MAX_PREFETCHES = 4
// MUST equal usePairFeed's initial seed limits (TRADE_FETCH_LIMIT for both
// sections) — the consume path is only reached where those exact limits are
// requested, so a key hit is always a parameter-identical request.
const SEED_LIMITS = { trades: 50, activity: 50 }

interface Entry {
  startedAt: number
  promise: Promise<PairSeedResponse>
  controller: AbortController
  settled: boolean
  /** pointerdown upgraded this intent; pointerleave must not abort it. */
  committed: boolean
}

const entries = new Map<string, Entry>()

function seedKey(addr: string, mode?: PairRouteMode): string {
  return JSON.stringify([addr, mode?.phase ?? '', mode?.source ?? '', mode?.dex ?? ''])
}

function drop(key: string, entry: Entry): void {
  if (entries.get(key) !== entry) return
  entries.delete(key)
  if (!entry.settled) entry.controller.abort()
}

function prune(now: number): void {
  for (const [key, entry] of entries) {
    if (now - entry.startedAt >= PREFETCH_TTL_MS) drop(key, entry)
  }
  while (entries.size >= MAX_PREFETCHES) {
    const oldest = entries.keys().next().value
    if (oldest == null) break
    const entry = entries.get(oldest)
    if (entry) drop(oldest, entry)
  }
}

function startPairSeedPrefetch(addr: string, mode: PairRouteMode | undefined, committed: boolean) {
  if (!addr) return
  const now = Date.now()
  const key = seedKey(addr, mode)
  const existing = entries.get(key)
  if (existing && now - existing.startedAt < PREFETCH_TTL_MS) {
    existing.committed ||= committed
    return
  }
  if (existing) drop(key, existing)
  prune(now)

  // Data and code are one user-visible dependency: a hot API snapshot still
  // misses the click budget if the PairPage/chart chunk starts only on click.
  void loadPairPage().catch(() => undefined)

  // Exactly one speculative (uncommitted) route owns an in-flight seed read:
  // a cursor sweep across rows cannot fan out N composed requests. Settled
  // snapshots remain reusable until TTL.
  for (const [otherKey, entry] of entries) {
    if (!entry.settled && !entry.committed) drop(otherKey, entry)
  }

  const controller = new AbortController()
  const entry: Entry = {
    startedAt: now,
    promise: fetchPairSeed(addr, SEED_LIMITS, { signal: controller.signal }, mode),
    controller,
    settled: false,
    committed,
  }
  entries.set(key, entry)
  // A rejected speculative read must not become an unhandled rejection when
  // the user moves away instead of opening the row — and must never be
  // consumed later, so drop it; the click path then fetches normally.
  void entry.promise.then(
    () => {
      entry.settled = true
    },
    () => {
      entry.settled = true
      drop(key, entry)
    }
  )
}

export function schedulePairRoutePrefetch(addr: string, mode?: PairRouteMode): void {
  startPairSeedPrefetch(addr, mode, false)
}

export function commitPairRoutePrefetch(addr: string, mode?: PairRouteMode): void {
  startPairSeedPrefetch(addr, mode, true)
}

export function cancelPairRoutePrefetch(addr: string, mode?: PairRouteMode): void {
  const key = seedKey(addr, mode)
  const entry = entries.get(key)
  // Committed (pointerdown) intent survives pointerleave; it is about to be
  // consumed by the navigation and aborting it would waste the head start.
  if (entry && !entry.settled && !entry.committed) drop(key, entry)
}

// Consume-once handoff for usePairFeed's initial seed. Returns the in-flight
// or resolved seed promise only when the entry is younger than the TTL; the
// entry is removed either way, so a later mount or reseed can never replay
// stale data. The caller's barrier/coverage flow is unchanged — this promise
// is simply the initial seed read, already started.
export function consumePairSeedPrefetch(
  addr: string,
  mode?: PairRouteMode
): Promise<PairSeedResponse> | undefined {
  const key = seedKey(addr, mode)
  const entry = entries.get(key)
  if (!entry) return undefined
  entries.delete(key)
  if (Date.now() - entry.startedAt >= PREFETCH_TTL_MS) {
    if (!entry.settled) entry.controller.abort()
    return undefined
  }
  return entry.promise
}

export function clearPairRoutePrefetchForTest(): void {
  for (const [key, entry] of entries) drop(key, entry)
}
